"use client";

import { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";
import FashionProductCard from "@/components/fashion/common/FashionProductCard";
import { isCoolingDown, setCooldown } from "@/lib/popupCooldown";
import { getVisitedCategories } from "@/lib/visitedCategories";
import "./FashionBrowsePopup.scss";

const BROWSE_KEY     = "fashion_browse_popup";
const NEWSLETTER_KEY = "fashion_newsletter_popup";
const STOREFRONT_ID  = process.env.NEXT_PUBLIC_STOREFRONT_ID || "23";
const DELAY_MS       = 90000;
const VISIBLE_COUNT  = 4;

export default function FashionBrowsePopup() {
  const [visible, setVisible]   = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [offset, setOffset]     = useState(0);
  const [currency, setCurrency] = useState("PKR");

  useEffect(() => {
    const fashionCurrency = Cookies.get("fashionCurrencyName");
    if (fashionCurrency) setCurrency(fashionCurrency);
  }, []);

  // Re-fetch when currency changes
  useEffect(() => {
    const handleStorageChange = () => {
      const fashionCurrency = Cookies.get("fashionCurrencyName");
      if (fashionCurrency && fashionCurrency !== currency) {
        setCurrency(fashionCurrency);
        setLoading(true);
        setOffset(0);
        fetchProducts();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [currency]);

  const fetchProducts = useCallback(async () => {
    const visitedCategories = getVisitedCategories();

    // No visited categories → don't show the popup at all
    if (!visitedCategories.length) {
      setLoading(false);
      return;
    }

    // Use the most recently visited category
    const categoryHandle = visitedCategories[visitedCategories.length - 1];
    const storefrontId   = Cookies.get("fashionCurrencyId") || STOREFRONT_ID;

    try {
      const url = `${process.env.NEXT_PUBLIC_BACKEND}/storefront/product_list?storefront=${storefrontId}&category_handle=${encodeURIComponent(categoryHandle)}&limit=12`;
      const res  = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProducts(data?.data ?? []);
    } catch (err) {
      console.error("[FashionBrowsePopup]", err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const show = useCallback(() => {
    if (isCoolingDown(BROWSE_KEY)) return;

    // Only show if the user has visited at least one category
    const visitedCategories = getVisitedCategories();
    if (!visitedCategories.length) return;

    setVisible(true);
    fetchProducts();
  }, [fetchProducts]);

  // Re-fetch and update products when user visits a new category (same tab)
  useEffect(() => {
    const onCategoryUpdate = () => {
      if (visible) {
        setLoading(true);
        setOffset(0);
        fetchProducts();
      }
    };
    window.addEventListener("visitedCategoriesUpdated", onCategoryUpdate);
    return () => window.removeEventListener("visitedCategoriesUpdated", onCategoryUpdate);
  }, [visible, fetchProducts]);

  useEffect(() => {
    if (isCoolingDown(BROWSE_KEY)) return;

    // Newsletter already dismissed → start 30s timer
    if (isCoolingDown(NEWSLETTER_KEY)) {
      const t = setTimeout(show, DELAY_MS);
      return () => clearTimeout(t);
    }

    // Wait for newsletter to be dismissed in this tab
    let t = null;
    const onStorage = (e) => {
      if (e.key === NEWSLETTER_KEY && e.newValue && !isCoolingDown(BROWSE_KEY)) {
        t = setTimeout(show, DELAY_MS);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      if (t) clearTimeout(t);
    };
  }, [show]);

  const handleClose = () => {
    setCooldown(BROWSE_KEY);
    setVisible(false);
  };

  const visibleProducts = products.slice(offset, offset + VISIBLE_COUNT);
  const canPrev = offset > 0;
  const canNext = offset + VISIBLE_COUNT < products.length;

  // Don't render if not visible, or if no visited categories and not loading
  if (!visible) return null;
  if (!loading && products.length === 0) return null;

  return (
    <div className="fbp__overlay" onClick={handleClose}>
      <div className="fbp__modal" onClick={(e) => e.stopPropagation()}>
        <button className="fbp__close" onClick={handleClose} aria-label="Close">×</button>
        <h2 className="fbp__title">Can&apos;t find what you&apos;re looking for?</h2>
        <div className="fbp__divider" />

        {loading ? (
          <div className="fbp__loading">
            {[1, 2, 3, 4].map((i) => <div key={i} className="fbp__skeleton" />)}
          </div>
        ) : (
          <div className="fbp__carousel-wrapper">
            {canPrev && (
              <button
                className="fbp__arrow fbp__arrow--prev"
                onClick={() => setOffset((o) => o - 1)}
                aria-label="Previous"
              >‹</button>
            )}
            <div className="fbp__grid">
              {visibleProducts.map((product) => (
                <div key={product.id || product.handle} onClick={handleClose}>
                  <FashionProductCard product={product} currency={currency} />
                </div>
              ))}
            </div>
            {canNext && (
              <button
                className="fbp__arrow fbp__arrow--next"
                onClick={() => setOffset((o) => o + 1)}
                aria-label="Next"
              >›</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
